/**
 * @module cercaliagl/Bounds
 */
import LonLat from './LonLat.js';
import { distanceBetweenPoints } from './util.js';

/**
 * @typedef {Object} WrapDateLineOptions
 * @property {number|undefined} leftTolerance
 * @property {number|undefined} rightTolerance
 */

/**
 * @typedef {Object} BoundsOptions
 * @property {boolean} animation Animation.
 * @property {number} bearing Map bearing
 * @property {number} duration Duration if animation is enabled. Default `2000`.
 * @property {number} maxZoom Maximum zoom level when fitting the bounds.
 * @property {number | BoundsPadding} padding Padding in pixels from the bounds to the map canvas.
 * @property {number} pitch Map pitch.
 */

/**
 * @typedef {Object} BoundsPadding
 * @property {boolean} top Padding in pixels from the top of the map canvas.
 * @property {boolean} bottom Padding in pixels from the bottom of the map canvas.
 * @property {number} left Padding in pixels from the left of the map canvas.
 * @property {boolean} right Padding in pixels from the right of the map canvas.
 */

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const bounds = new cercaliagl.Bounds();
 * ```
 *
 * Bounding box classes with multiple methods. It's a rectangle with geographical coordinates (WGS84 - https://es.wikipedia.org/wiki/WGS84):<br/>
 * This is useful for some spatial calculations.
 * <br/>
 * Coordinates order in array is: `[left, bottom, right, top]`.
 *
 * Example usage:
 *
 * ```js
 *   const bounds = new cercaliagl.Bounds([-23.62473534670107,31.285369382485797,18.56276465329893,47.98055500672558]);
 *   map.fitBounds(bounds); // Fit map to bounds
 *   const marker = new cercaliagl.Marker({ position: bounds.getCenterLonLat(), simpleLabel: new cercaliagl.SimpleLabe({text: 'Center bbox'})});
 *   map.addMarker(marker);
 * ```
 *
 * @api
 */
class Bounds {
  /**
   * @param {Array.<number>=} [arrCoord] Coordinates `[left, bottom, right, top]`. Coordinates in `EPSG:4326` projection.
   * @param {BoundsOptions} {boundsOptions} Bounds options. Default: `undefined`.
   */
  constructor(arrCoord, boundsOptions) {
    /**
     * @private
     * @type {number|undefined}
     */
    this.left_ = 0;

    /**
     * @private
     * @type {number|undefined}
     */
    this.bottom_ = 0;

    /**
     * @private
     * @type {number|undefined}
     */
    this.right_ = 0;

    /**
     * @type {number|undefined}
     * @private
     */
    this.top_ = 0;

    /**
     * @type {Array.<number>|undefined}
     * @private
     */
    this.centerLonLat_ = undefined;

    /**
     * @private
     * @type {BoundsOptions|undefined}
     */
    this.boundsOptions = boundsOptions;

    if (arrCoord && Array.isArray(arrCoord)) {
      this.initialize_(arrCoord);
    }
  }

  /**
   * Cleans the object bounds. It reinitializes.
   * @api
   */
  clear() {
    this.top_ = undefined;
    this.right_ = undefined;
    this.bottom_ = undefined;
    this.left_ = undefined;
    this.centerLonLat_ = undefined;
  }

  /**
   * Check if bounds are emty or have been initialized yet.
   * @return {boolean} `true` if empty, else `false`.
   * @api
   */
  isEmpty() {
    return this.top_ === undefined && this.right_ === undefined && this.bottom_ === undefined && this.left_ === undefined;
  }

  /**
   * Returns the top limit for Y axis.
   * @api
   * @return {number|undefined} top
   */
  getTop() {
    return this.top_;
  }

  /**
   * Returns the right limit for X axis.
   * @return {number|undefined} right
   * @api
   */
  getRight() {
    return this.right_;
  }

  /**
   * Returns the lower limit for Y axis.
   * @return {number|undefined} bottom
   * @api
   */
  getBottom() {
    return this.bottom_;
  }

  /**
   * Returns the left limit for X axis.
   * @return {number|undefined} left
   * @api
   */
  getLeft() {
    return this.left_;
  }

  /**
   * Returns the North West coordinate
   * @return {LonLat|undefined} Northwest coordinate
   * @api
   */
  getNorthWest() {
    if (this.left_ !== undefined && this.top_ !== undefined) {
      return new LonLat(this.left_, this.top_);
    }
    return undefined;
  }

  /**
   * Returns the South West coordinate
   * @return {LonLat|undefined} Southwest coordinate
   * @api
   */
  getSouthWest() {
    if (this.left_ !== undefined && this.bottom_ !== undefined) {
      return new LonLat(this.left_, this.bottom_);
    }
    return undefined;
  }

  /**
   * Returns the North East coordinate
   * @return {LonLat|undefined} array NorthEast coordinate
   * @api
   */
  getNorthEast() {
    if (this.right_ !== undefined && this.top_ !== undefined) {
      return new LonLat(this.right_, this.top_);
    }
    return undefined;
  }

  /**
   * Returns the South East coordinate
   * @return {LonLat|undefined} SouthEast coordinate
   * @api
   */
  getSouthEast() {
    if (this.right_ !== undefined && this.bottom_ !== undefined) {
      return new LonLat(this.right_, this.bottom_);
    }
    return undefined;
  }

  /**
   * Returns the Bounds Options
   * @return {BoundsOptions|undefined} Bounds Options
   * @api
   */
  getBoundsOptions() {
    return this.boundsOptions;
  }

  /**
   * Sets the Bounds options
   * @param {BoundsOptions|undefined} boundsOptions
   * @api
   */
  setBoundsOptions(boundsOptions) {
    this.boundsOptions = boundsOptions;
  }

  /**
   * Reset if give a bounds
   * @private
   * @param {Array.<number>} coordinates Coordinates `[left, bottom, right, top]`
   */
  initialize_(coordinates) {
    [this.left_, this.bottom_, this.right_, this.top_] = coordinates;
  }

  /**
   * Clone object
   * @return {Bounds} Object cloned
   * @api
   */
  clone() {
    return new Bounds([this.left_, this.bottom_, this.right_, this.top_]);
  }

  /**
   * Returns an object increasing (`* 0.5`) all the limits (top, bottom, left, right).
   * @return {Bounds} bounds doubled by 0.5 for each limit.
   * @api
   */
  getDouble() {
    const dX = this.right_ - this.left_;
    const dY = this.top_ - this.bottom_;
    return new Bounds([this.left_ - dX / 2, this.bottom_ - dY / 2, this.right_ + dX / 2, this.top_ + dY / 2]);
  }

  /**
   * @param {number} percentage to increase for every limit (top, bottom, left, right)
   * @return {Bounds} Returns the object 'Bounds' increased
   * @api
   */
  cloneIncreasingPercentage(percentage) {
    const vPercentage = percentage / 100;
    const dX = this.right_ - this.left_;
    const dY = this.top_ - this.bottom_;
    return new Bounds([
      this.left_ - dX * vPercentage,
      this.bottom_ - dY * vPercentage,
      this.right_ + dX * vPercentage,
      this.top_ + dY * vPercentage,
    ]);
  }

  /**
   * @param {Bounds} bounds Bounds to compare
   * @return {boolean} Returns if bounds have the same limits
   * @api
   */
  equals(bounds) {
    let equals = false;
    if (bounds !== null) {
      equals = this.left_ === bounds.getLeft()
        && this.right_ === bounds.getRight()
        && this.top_ === bounds.getTop()
        && this.bottom_ === bounds.getBottom();
    }
    return equals;
  }

  /**
   * Returns the object in string [left, bottom, right, top]
   * @return {string} Returns a string with the bounds. Format: 'left,bottom,right,top'
   * @override
   * @api
   */
  toString() {
    return [this.left_, this.bottom_, this.right_, this.top_].join(',');
  }

  /**
   * @return {Array.<number>} Returns an array with the bounds. Format:   [left, bottom, right, top]
   * @api
   */
  toArray() {
    return [this.left_, this.bottom_, this.right_, this.top_];
  }

  /**
   * @return {string} Returns BoundingBox-String '0.41,41.24,0.541,40.51'
   * @api
   */
  toBBOX() {
    return `${this.left_},${this.bottom_},${this.right_},${this.top_}`;
  }

  /**
   * @return {string} Returns BoundingBox in WKT format
   * @api
   */
  toWKT() {
    return `POLYGON((${this.left_} ${this.top_},${this.right_} ${this.top_},${this.right_} ${this.bottom_},${this.left_} ${this.bottom_},${this.left_} ${this.top_}))`;
  }

  /**
   * Convert bounds to double array. Format: `[[left, bottom], [right, top]]`
   * @return {Array.<Array.<number>>}
   * @api
   */
  toDoubleArray() {
    return [[this.left_, this.bottom_], [this.right_, this.top_]];
  }

  /**
   * Width limits
   * @return {number} Bounds width
   * @api
   */
  getWidth() {
    return this.right_ - this.left_;
  }

  /**
   * Height limits.
   * @return {number} Height bounds
   * @api
   */
  getHeight() {
    return this.top_ - this.bottom_;
  }

  /**
   * Array with size limits `[width,height]`
   * @return {Array.<number>} Size bounds `[width, height]`
   * @api
   */
  getSize() {
    return [this.getWidth(), this.getHeight()];
  }

  /**
   * Returns the bounds center
   * @return {LonLat|undefined} Map center
   * @api
   */
  getCenterLonLat() {
    if (this.centerLonLat_ === undefined) {
      this.centerLonLat_ = [(this.left_ + this.right_) / 2, (this.bottom_ + this.top_) / 2];
    }
    return new LonLat(this.centerLonLat_[0], this.centerLonLat_[1]);
  }

  /**
   * Move the limits horizontally and vertically
   * @param {number} x Displacement X axis
   * @param {number} y Displacement Y axis
   * @return {Bounds} Bounds after add
   * @api
   */
  add(x, y) {
    return new Bounds([this.left_ + x, this.bottom_ + y, this.right_ + x, this.top_ + y]);
  }

  /**
   * Expand the limits for including coordinates {ol.Coordinate} or more limits {Bounds}.
   * @param {LonLat|Bounds|null} boundsOrLonLat `Bounds` or `LonLat` to extend current bounds
   * @api
   */
  extend(boundsOrLonLat) {
    if (boundsOrLonLat !== null) {
      if (boundsOrLonLat instanceof Bounds) {
        this.centerLonLat_ = undefined;

        if (this.left_ === 0 || boundsOrLonLat.getLeft() < this.left_) {
          this.left_ = boundsOrLonLat.getLeft();
        }
        if (this.bottom_ === 0 || boundsOrLonLat.getBottom() < this.bottom_) {
          this.bottom_ = boundsOrLonLat.getBottom();
        }
        if (this.right_ === 0 || boundsOrLonLat.getRight() > this.right_) {
          this.right_ = boundsOrLonLat.getRight();
        }
        if (this.top_ === 0 || boundsOrLonLat.getTop() > this.top_) {
          this.top_ = boundsOrLonLat.getTop();
        }
      } else {
        this.extendXY(boundsOrLonLat.getLon(), boundsOrLonLat.getLat());
      }
    }
  }

  /**
   * Expand the limits for including the coordinate XY. Format `[x, y]`
   * @param {number} x Longitud
   * @param {number} y Latitud
   * @api
   */
  extendXY(x, y) {
    // clear cached center location
    this.centerLonLat_ = undefined;

    if (this.left_ === 0 || x < this.left_) {
      this.left_ = x;
    }
    if (this.bottom_ === 0 || y < this.bottom_) {
      this.bottom_ = y;
    }
    if (this.right_ === 0 || x > this.right_) {
      this.right_ = x;
    }
    if (this.top_ === 0 || y > this.top_) {
      this.top_ = y;
    }
  }

  /**
   * Returns true if a coordinate (LonLat object) is inside the bounds
   * @param {LonLat} lonLat Coordinate.
   * @return {boolean} `true` or `false`.
   * @api
   */
  containsLonLat(lonLat) {
    const contains = this.contains(lonLat.getLon(), lonLat.getLat());
    return contains;
  }

  /**
   * Returns if it's inside the object bounds
   * del objeto
   * @param {number|undefined} x Latitude.
   * @param {number|undefined} y Longitude.
   * @param {boolean} [inclusive]  Inclusive, with the bounds.
   * @return {boolean} Returns if it's inside the object bounds.
   * @api
   */
  contains(x, y, inclusive = true) {
    if (x === undefined || y === undefined) {
      return false;
    }

    let contains = false;
    if (inclusive) {
      contains = x >= this.left_ && x <= this.right_ && y >= this.bottom_ && y <= this.top_;
    } else {
      contains = x > this.left_ && x < this.right_ && y > this.bottom_ && y < this.top_;
    }

    return contains;
  }

  /**
   * Determina si unos limites intersecta con los limites del objeto actual. Se
   * considera que intersectan si uno de sus aristas se encuentran dentro
   * de los limites del otro
   * @param {Bounds} bounds Bounds
   * @param {Object} options Options
   * @return {boolean} `true` if bounds intersects.
   * @api
   */
  intersectsBounds(bounds, options) {
    let thisBounds;
    let _options = options;
    let _bounds = bounds;

    if (typeof options === 'boolean') {
      _options = {
        inclusive: options,
      };
    }
    _options = options || {};

    if (_options.worldBounds) {
      thisBounds = this.wrapDateLine(_options.worldBounds);
      _bounds = bounds.wrapDateLine(_options.worldBounds);
    } else {
      thisBounds = this;
    }

    if (_options.inclusive === null) {
      _options.inclusive = true;
    }

    let intersects = false;
    const mightTouch = thisBounds.left_ === _bounds.getRight()
      || thisBounds.right_ === _bounds.getLeft()
      || thisBounds.top_ === _bounds.getBottom()
      || thisBounds.bottom_ === _bounds.getTop();

    // if the two bounds only touch at an edge, and inclusive is false,
    // then the bounds don't *really* intersect.
    if (_options.inclusive || !mightTouch) {
      // otherwise, if one of the boundaries even partially contains another,
      // inclusive of the edges, then they do intersect.
      const inBottom = (_bounds.getBottom() >= thisBounds.bottom_ && _bounds.getBottom() <= thisBounds.top_)
        || (thisBounds.bottom_ >= _bounds.getBottom() && thisBounds.bottom_ <= _bounds.getTop());
      const inTop = (_bounds.getTop() >= thisBounds.bottom_ && _bounds.getTop() <= thisBounds.top_)
        || (thisBounds.top_ > _bounds.getBottom() && thisBounds.top_ < _bounds.getTop());
      const inLeft = (_bounds.getLeft() >= thisBounds.left_ && _bounds.getLeft() <= thisBounds.right_)
        || (thisBounds.left_ >= bounds.getLeft() && thisBounds.left_ <= bounds.getRight());
      const inRight = (_bounds.getRight() >= thisBounds.left_ && _bounds.getRight() <= thisBounds.right_)
        || (thisBounds.right_ >= _bounds.getLeft() && thisBounds.right_ <= _bounds.getRight());
      intersects = (inBottom || inTop) && (inLeft || inRight);
    }

    if (_options.worldBounds && !intersects) {
      const world = _options.worldBounds;
      const width = world.getWidth();
      const selfCrosses = !world.containsBounds(thisBounds);
      const boundsCrosses = !world.containsBounds(_bounds);
      if (selfCrosses && !boundsCrosses) {
        _bounds = _bounds.add(-width, 0);
        intersects = thisBounds.intersectsBounds(bounds, {
          inclusive: _options.inclusive,
        });
      } else if (boundsCrosses && !selfCrosses) {
        thisBounds = thisBounds.add(-width, 0);
        intersects = _bounds.intersectsBounds(thisBounds, {
          inclusive: _options.inclusive,
        });
      }
    }

    return intersects;
  }

  /**
   * @param {Bounds} bounds Bounds.
   * @param {boolean} [partial] If one destination corner is inside the bounds consider contains the bounds.
   * The default value is false. If false, all the destination bounds must be inside this bounds.
   * @param {boolean} [inclusive] Include edges. Default value: true
   * @return {boolean} Returns if contains bounds inside object
   * @api
   */
  containsBounds(bounds, partial = false, inclusive = true) {
    if (bounds.getLeft() && bounds.getBottom() && bounds.getTop() && bounds.getRight()) {
      const bottomLeft = this.contains(bounds.getLeft(), bounds.getBottom(), inclusive);
      const bottomRight = this.contains(bounds.getRight(), bounds.getBottom(), inclusive);
      const topLeft = this.contains(bounds.getLeft(), bounds.getTop(), inclusive);
      const topRight = this.contains(bounds.getRight(), bounds.getTop(), inclusive);

      return partial ? bottomLeft || bottomRight || topLeft || topRight : bottomLeft && bottomRight && topLeft && topRight;
    }
    return false;
  }

  /**
   * @param {Bounds} maxExtent Max extent
   * @param {WrapDateLineOptions} [options] Optional value. `options.leftTolerance|options.rightTolerance`
   * @return {Bounds} Bounds
   * @api
   */
  wrapDateLine(maxExtent, options = {}) {
    const leftTolerance = options.leftTolerance || 0;
    const rightTolerance = options.rightTolerance || 0;

    let newBounds = this.clone();

    if (maxExtent) {
      const width = maxExtent.getWidth();

      // shift right?
      while (newBounds.getLeft() < maxExtent.getLeft() && newBounds.getRight() - rightTolerance <= maxExtent.getLeft()) {
        newBounds = newBounds.add(width, 0);
      }

      // shift left?
      while (newBounds.getLeft() + leftTolerance >= maxExtent.getRight() && newBounds.getRight() > maxExtent.getRight()) {
        newBounds = newBounds.add(-width, 0);
      }

      // crosses right only? force left
      const newLeft = newBounds.getLeft() + leftTolerance;
      if (newLeft < maxExtent.getRight() && newLeft > maxExtent.getLeft() && newBounds.getRight() - rightTolerance > maxExtent.getRight()) {
        newBounds = newBounds.add(-width, 0);
      }
    }

    return newBounds;
  }

  /**
   * Get bounds width distance in meters.
   * @return {number|undefined} Bounds width distance in meters.
   * @api
   */
  getWidthInMeters() {
    const ne = this.getNorthEast();
    const nw = this.getNorthWest();
    if (ne !== undefined && nw !== undefined) {
      return Math.abs(distanceBetweenPoints(ne, nw));
    }
    return undefined;
  }

  /**
   * Get height width distance in meters.
   * @return {number|undefined} Bounds height distance in meters.
   * @api
   */
  getHeightInMeters() {
    const ne = this.getNorthEast();
    const se = this.getSouthEast();
    if (ne !== undefined && se !== undefined) {
      return Math.abs(distanceBetweenPoints(ne, se));
    }
    return undefined;
  }
}

export default Bounds;
